import {IApiEndpoint, IPagination} from "@localization/siteMap/siteMapInterfaces.ts";
import type {IFormaPagamento, IFormaPagamentoFormEntry} from "@models/formaPagamento/IFormaPagamento.ts";
import {IArquivoOrcamento} from '@models/sistema/IArquivo.ts';
import {siteMapApi} from "../siteMap";
import {ISolicitacaoOrcamento} from "@models/solicitacoes/orcamento.ts";
import {IFlag} from "@models/shared/IFlag.ts";
import {ISolicitacaoOrcamentoFlag} from "@models/orcamento/ISolicitacaoOrcamentoFlag.ts";
import {OrcamentoRastreamentoFilter} from "@models/orcamento/OrcamentoRastreamentoFilter.ts";
import {ISolicitacaoOrcamentoRastreio} from "@models/orcamento/ISolicitacaoOrcamentoRastreio.ts";
import {Dayjs} from "dayjs";
import {IDataNoPageDto} from "@models/shared/IDataDto.ts";

export const _OrcamentosEndpoints = {
    getFormasPagamentoPesquisa: (search: string, pagination: IPagination): IApiEndpoint => ({
        url: '/api/Orcamentos/GetFormasPagamentoPesquisa',
        pagination: pagination,
        query: {search},
        method: "GET"
    }),
    CreateFormaPagamento: (data: IDataNoPageDto<IFormaPagamento>): IApiEndpoint<IFormaPagamento> => ({
        url: '/api/Orcamentos/CreateFormaPagamento',
        data: data,
        method: "POST"
    }),
    updateDadosPagamento: (entries: IFormaPagamentoFormEntry[]): IApiEndpoint => ({
        url: '/api/Orcamentos/UpdateDadosPagamento',
        body: entries,
        method: "PUT"
    }),
    GetOrcamentoAnexo: (id: number, tipo: IArquivoOrcamento['tipo'] = 'Orcamento'): IApiEndpoint<IArquivoOrcamento> => ({
        url: `/api/Orcamentos/GetOrcamentoAnexo`,
        method: "GET",
        query: {tipoArquivo: tipo},
        path: [id]
    }),
    GetOrcamentoAnexos: (orcamentoId: number, tipo: IArquivoOrcamento['tipo']): IApiEndpoint<IArquivoOrcamento[]> => ({
        url: `/api/Orcamentos/GetOrcamentoAnexos`,
        query: {tipoArquivo: tipo},
        method: "GET",
        path: [orcamentoId]
    }),
    GetOrcamentoAnexoBytesByNome: (nome: string, tipoArquivo: IArquivoOrcamento['tipo']): IApiEndpoint<Blob> => ({
        url: `/api/Orcamentos/GetOrcamentoAnexoBytesByNome`,
        query: {nome, tipoArquivo},
        method: "GET"
    }),
    CreateAnexoOrcamento: (body: Pick<IArquivoOrcamento, 'orcamentoId' | 'prefixo' | 'extensao' | 'conteudoBase64' | 'tipo'>): IApiEndpoint<IArquivoOrcamento> => ({
        url: '/api/Orcamentos/CreateAnexoOrcamento',
        body: body,
        method: "POST"
    }),
    /**
     * Confirma o orçamento do ponto de vista do solicitante.
     * @param orcamentoId
     * @constructor
     */
    ConfirmarOrcamento: ({orcamentoId}: { orcamentoId: number }): IApiEndpoint<ISolicitacaoOrcamento> => ({
        url: `/api/Orcamentos/ConfirmaOrcamento/${orcamentoId}`,
        method: "POST",
    })
    ,
    /**
     * Confirma o pagamento do orçamento.
     * @param orcamentoId
     * @constructor
     */
    ConfirmarPagamento: ({orcamentoId}: { orcamentoId: number }): IApiEndpoint<ISolicitacaoOrcamento> => ({
        url: `/api/Orcamentos/ConfirmaPagamento/${orcamentoId}`,
        method: "POST",
    }),
    /**
     * Confirma que a compra do orçamento foi feita.
     * @param orcamentoId
     * @constructor
     */
    ConfirmarOrcamentoCompra: ({orcamentoId}: { orcamentoId: number }): IApiEndpoint<ISolicitacaoOrcamento> => ({
        url: `/api/Orcamentos/ConfirmaCompraOrcamento/${orcamentoId}`,
        method: "POST",
    }),
    AutorizarCompra: ({orcamentoId}: { orcamentoId: number }): IApiEndpoint<ISolicitacaoOrcamento> => ({
        url: `/api/Orcamentos/AutorizaCompra/${orcamentoId}`,
        method: "POST",
    }),
    ConfirmaEntregaOrcamento: ({orcamentoId}: { orcamentoId: number }): IApiEndpoint<ISolicitacaoOrcamento> => ({
        url: `/api/Orcamentos/ConfirmaEntregaOrcamento/${orcamentoId}`,
        method: "POST",
    }),
    FinalizarOrcamento: ({idOrcamento}: { idOrcamento: number }): IApiEndpoint<ISolicitacaoOrcamento> => ({
        url: `/api/Orcamentos/FinalizaOrcamento/${idOrcamento}`,
        method: "POST",
    }),
    RejeitarOrcamento: ({orcamentoId, motivo}: {
        orcamentoId: number,
        motivo: string
    }): IApiEndpoint<ISolicitacaoOrcamento> => ({
        url: `/api/Orcamentos/RejeitaOrcamento/${orcamentoId}`,
        query: {motivo},
        method: "POST",
    }),
    GetFlagsOrcamento: ({idOrcamento}: { idOrcamento?: number }, pagination: IPagination): IApiEndpoint<IFlag[]> => ({
        url: '/api/Orcamentos/GetFlagsOrcamento',
        query: {idOrcamento},
        pagination: pagination,
        method: "GET"
    }),
    SetFlagOrcamento: (flag: ISolicitacaoOrcamentoFlag): IApiEndpoint<ISolicitacaoOrcamentoFlag[]> => ({
        url: '/api/Orcamentos/SetFlagOrcamento',
        body: flag,
        method: "POST"
    }),
    CreateCodigoRastreio: ({idOrcamento, codigo, transportadoraId, dataPrevisaoEntrega}: {
        idOrcamento: number,
        codigo: string,
        transportadoraId?: number,
        dataPrevisaoEntrega?: Dayjs
    }): IApiEndpoint<ISolicitacaoOrcamento> => ({
        url: `/api/Orcamentos/CreateCodigoRastreio/${idOrcamento}`,
        query: {codigo, transportadoraId, dataPrevisaoEntrega},
        method: "POST"
    }),
    GetRastreamentos: (filters: OrcamentoRastreamentoFilter, pagination: IPagination): IApiEndpoint<ISolicitacaoOrcamentoRastreio[]> => ({
        url: '/api/Orcamentos/GetRastreamentos',
        query: filters,
        pagination: pagination,
        method: "GET"
    }),
    DisableRastreamento: ({rastreamentoId}: {
        rastreamentoId: number
    }): IApiEndpoint<ISolicitacaoOrcamentoRastreio> => ({
        url: `/api/Orcamentos/DisableRastreamento/${rastreamentoId}`,
        method: "DELETE"
    }),
    FinalizaRastreamento: ({rastreamentoId}: {
        rastreamentoId: number
    }): IApiEndpoint<ISolicitacaoOrcamentoRastreio> => ({
        url: `/api/Orcamentos/FinalizaRastreamento/${rastreamentoId}`,
        method: "PUT"
    })
}

export const GetFormasPagamentoPesquisa = (search: string, pagination: IPagination) => {
    const endpoint = _OrcamentosEndpoints.getFormasPagamentoPesquisa(search, pagination);

    return siteMapApi.request<IFormaPagamento[]>(endpoint)
}

export const UpdateDadosPagamento = (entries: IFormaPagamentoFormEntry[]) => {
    const endpoint = _OrcamentosEndpoints.updateDadosPagamento(entries);

    return siteMapApi.request<ISolicitacaoOrcamento[]>(endpoint)
}